import { useCallback, useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useLocaleDateFormatter } from './useLocaleDateFormatter';
import {
  useTripByIdQuery,
  usePartialUpdateTripMutation,
} from './useTripsQueries';
import {
  useAddMembershipVisitMutation,
  useUpdateMembershipVisitMutation,
  useDeleteMembershipVisitMutation,
  useCreateMyMembershipMutation,
} from './useMembershipVisitQueries';
import {
  errorHandler,
  isProcessedError,
  type ProcessedError,
} from '../services/utils/errorHandler';
import { buildTravelerViews } from '../utils/trips/travelerColorsUtils';
import { buildSummaryFormState } from '../utils/trips/buildSummaryFormState';
import { buildSummaryRequest } from '../utils/trips/buildSummaryRequest';
import type { TripFormState, MembershipVisitRequest } from '../types';
import { isAdmin as checkIsAdmin } from '../utils/trips/isAdmin';

export function useItineraryOverview(tripId: string) {
  const { user } = useAuth();
  const { locale } = useLocale();
  const { formatDisplayDate } = useLocaleDateFormatter();
  const tripQuery = useTripByIdQuery(tripId);
  const updateTripMutation = usePartialUpdateTripMutation();
  const createMembershipMutation = useCreateMyMembershipMutation(tripId);
  const addVisitMutation = useAddMembershipVisitMutation(tripId);
  const updateVisitMutation = useUpdateMembershipVisitMutation(tripId);
  const deleteVisitMutation = useDeleteMembershipVisitMutation(tripId);

  const [error, setError] = useState<ProcessedError | null>(null);
  const [activeTravelerId, setActiveTravelerId] = useState<number | null>(null);
  const [showNewVisitRow, setShowNewVisitRow] = useState(false);
  const [headerForm, setHeaderForm] = useState<TripFormState>({
    name: '',
    description: '',
    visibility: 'MEMBERS',
    status: 'PLANNING',
    occasions: [],
    lockEnabled: false,
    lockStartDate: '',
    lockEndDate: '',
    budgetEnabled: false,
    budgetAmount: '',
    budgetCurrency: 'USD',
  });

  const trip = tripQuery.data;
  const isAdmin = trip != null ? checkIsAdmin(trip, user?.id) : false;

  const currentUserMembership = useMemo(() => {
    if (!trip || !user) {
      return undefined;
    }
    return trip.memberships.find(m => m.traveler.id === user.id);
  }, [trip, user]);

  const travelers = useMemo(
    () => (trip ? buildTravelerViews(trip.memberships ?? [], user?.id) : []),
    [trip, user?.id]
  );

  const activeTraveler = useMemo(
    () => travelers.find(t => t.membership.traveler.id === activeTravelerId),
    [travelers, activeTravelerId]
  );

  const captureError = useCallback((err: unknown) => {
    setError(
      isProcessedError(err) ? err : errorHandler.processError(err as Error)
    );
  }, []);

  useEffect(() => {
    if (!trip) {
      return;
    }
    setHeaderForm(buildSummaryFormState(trip, locale));
  }, [trip, locale]);

  useEffect(() => {
    if (travelers.length === 0) {
      setActiveTravelerId(null);
      return;
    }
    if (
      activeTravelerId == null ||
      !travelers.some(t => t.membership.traveler.id === activeTravelerId)
    ) {
      // After membership bootstrap, handleCreateMembership selects the current
      // user before invalidateQueries refetch completes. Keep that selection
      // instead of falling back to another traveler tab.
      if (user && activeTravelerId === user.id) {
        return;
      }
      const current = travelers.find(t => t.isCurrentUser) ?? travelers[0];
      setActiveTravelerId(current.membership.traveler.id);
    }
  }, [travelers, activeTravelerId, user]);

  useEffect(() => {
    if (!tripQuery.error) {
      setError(null);
      return;
    }
    captureError(tripQuery.error);
  }, [tripQuery.error, captureError]);

  const handleResetHeader = useCallback(() => {
    if (!trip) {
      return;
    }
    setHeaderForm(buildSummaryFormState(trip, locale));
  }, [trip, locale]);

  const handleSaveShared = useCallback(async () => {
    if (!trip || !isAdmin) {
      return;
    }
    const patch = buildSummaryRequest(trip, headerForm);
    if (Object.keys(patch).length === 0) {
      notifications.show({
        title: 'No changes',
        message: 'Nothing to save.',
        color: 'blue',
        autoClose: 2000,
      });
      return;
    }
    try {
      await updateTripMutation.mutateAsync({ id: tripId, tripData: patch });
      notifications.show({
        title: 'Saved',
        message: 'Trip details updated.',
        color: 'green',
        autoClose: 2000,
      });
    } catch (err) {
      captureError(err);
    }
  }, [trip, isAdmin, headerForm, tripId, updateTripMutation, captureError]);

  const handleCreateMembership = useCallback(async () => {
    try {
      await createMembershipMutation.mutateAsync();
      if (user) {
        setActiveTravelerId(user.id);
      }
      notifications.show({
        title: 'Itinerary ready',
        message: 'You can now add membership visits.',
        color: 'green',
        autoClose: 2000,
      });
    } catch (err) {
      captureError(err);
    }
  }, [createMembershipMutation, captureError, user]);

  const handleAddVisit = useCallback(
    async (body: MembershipVisitRequest) => {
      try {
        await addVisitMutation.mutateAsync(body);
        setShowNewVisitRow(false);
        notifications.show({
          title: 'City added',
          message: 'Your visit leg was saved.',
          color: 'green',
          autoClose: 1500,
        });
      } catch (err) {
        captureError(err);
      }
    },
    [addVisitMutation, captureError]
  );

  const handleUpdateVisit = useCallback(
    async (visitId: number, body: MembershipVisitRequest) => {
      try {
        await updateVisitMutation.mutateAsync({ visitId, body });
        notifications.show({
          title: 'Updated',
          message: 'Visit leg saved.',
          color: 'green',
          autoClose: 1500,
        });
      } catch (err) {
        captureError(err);
      }
    },
    [updateVisitMutation, captureError]
  );

  const handleDeleteVisit = useCallback(
    async (visitId: number) => {
      try {
        await deleteVisitMutation.mutateAsync(visitId);
      } catch (err) {
        captureError(err);
      }
    },
    [deleteVisitMutation, captureError]
  );

  const busy =
    updateTripMutation.isPending ||
    createMembershipMutation.isPending ||
    addVisitMutation.isPending ||
    updateVisitMutation.isPending ||
    deleteVisitMutation.isPending;

  return {
    tripQuery,
    trip,
    user,
    error,
    headerForm,
    setHeaderForm,
    isAdmin,
    travelers,
    activeTravelerId,
    activeTraveler,
    currentUserMembership,
    showNewVisitRow,
    busy,
    formatSummaryDate: formatDisplayDate,
    setActiveTravelerId,
    setShowNewVisitRow,
    handleResetHeader,
    handleSaveShared,
    handleCreateMembership,
    handleAddVisit,
    handleUpdateVisit,
    handleDeleteVisit,
    isSavingTrip: updateTripMutation.isPending,
    isCreatingMembership: createMembershipMutation.isPending,
    isAddingVisit: addVisitMutation.isPending,
    isUpdatingVisit: updateVisitMutation.isPending,
    isDeletingVisit: deleteVisitMutation.isPending,
  };
}
